import { getOwnBackend } from '@/app/config/products.config';
import { useDataTablePageSync, useNavigation } from '@/shared/hooks';
import {
  Avatar,
  DataTable,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '../../../shared/ui';
import { PageHeader } from '../../../shared/ui/page';
import {
  f,
  formatCurrency,
  formatDateToBrazil,
  formatRegisterNumber,
  useFilters,
} from '../../../shared/utils';
import { useSelectedBrokerWriteGuard } from '../../brokers/hooks';
import { usePolicies } from '../hooks';
import { usePolicyPdf } from '../hooks/use-policy-pdf.hook';
import { useOwnBackendPolicyPdf } from '../hooks/use-own-backend-policy-pdf.hook';
import { useBrokerProducts } from '../../brokers/hooks';
import { useProducts } from '../../quotes/hooks/common/use-products.hook';
import { PolicyStatusCards } from './policy-status-cards.component';
import { CalendarDays, Download, Eye, RefreshCw, User } from 'lucide-react';
import { PolicyStatus } from '../../../shared/enums/policy-status.enum';
import type { PolicySummary } from '../../../services/policies/types';
import type { Broker } from '../../../services/brokers/types';
import {
  type PolicyAction,
  createPoliciesColumns,
} from './policies-table-columns';
import { DownloadPdfDialog } from './download-pdf.dialog';
import { Page } from '../../../shared/ui/page';

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Canceladas', value: String(PolicyStatus.Rejected) },
  { label: 'Emitidas', value: String(PolicyStatus.Effective) },
];

const DEFAULT_SORT_FIELD = 'ExternalEffectiveDate';
const DEFAULT_SORT_TYPE = 'desc';

const POLICY_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  insuredName: 'Nome',
  brokerName: 'AgentName',
  productName: 'ProductName',
  externalPolicyNo: 'ExternalPolicyNo',
  effectiveDate: 'ExternalEffectiveDate',
};

const POLICY_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'insuredName',
  AgentName: 'brokerName',
  ProductName: 'productName',
  ExternalPolicyNo: 'externalPolicyNo',
  ExternalEffectiveDate: 'effectiveDate',
};

export function PoliciesContent({
  selectedBroker,
}: {
  selectedBroker: Broker | null;
}) {
  const { push } = useNavigation();
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(1000),
    product: f.string().withDefault(''),
    status: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<PolicySummary>(filters.pageNo);

  const { data: brokerProducts } = useBrokerProducts(selectedBroker?.code);
  const { data: products } = useProducts();
  const policyPdf = usePolicyPdf();
  const ownBackendPolicyPdf = useOwnBackendPolicyPdf();
  // Com corretora selecionada todas as linhas são dela; em "Todos Corretores"
  // o guard não trava e a Api barra por linha (403).
  const policyActionGuard = useSelectedBrokerWriteGuard('policy', 'other');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingPolicy, setPendingPolicy] = useState<PolicySummary | null>(
    null
  );

  // Com um corretor selecionado, exibe apenas os produtos ativos daquele
  // corretor (inclui RD Equipamentos quando configurado). Sem corretor
  // ("Todos Corretores"), exibe todo o catálogo — inclusive Capital Global,
  // que já aparece na listagem (ListPoliciesHandler) e é filtrável pelo
  // ProductCode no índice do InsureMO.
  const productOptions = useMemo(() => {
    const items = selectedBroker
      ? (brokerProducts?.data ?? [])
          .filter(product => product.status === 'active')
          .map(product => ({ label: product.name, value: product.code }))
      : [...(products ?? [])]
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
          .map(p => ({ label: p.name, value: p.code }));
    return [{ label: 'Todos', value: '' }, ...items];
  }, [selectedBroker, brokerProducts, products]);

  const { data, isLoading, refetch, isFetching } = usePolicies({
    pageNumber: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    search: debouncedFilters.search,
    status: debouncedFilters.status,
    productCode: debouncedFilters.product || undefined,
    effectiveDateFrom: debouncedFilters.dateFrom || undefined,
    effectiveDateTo: debouncedFilters.dateTo || undefined,
    partyCode: selectedBroker?.code,
    sortField: debouncedFilters.sortField,
    sortType: debouncedFilters.sortType === 'desc' ? 'desc' : 'asc',
  });

  useEffect(() => {
    if (policyPdf.isSuccess && dialogOpen) {
      setDialogOpen(false);
      setPendingPolicy(null);
      policyPdf.reset();
    }
  }, [policyPdf.isSuccess]);

  const handleAction = (action: PolicyAction, policy: PolicySummary) => {
    if (action === 'download-pdf' && policyActionGuard.blocked) return;

    // Produto de backend próprio (registry): detalhes abrem na tela de
    // apólice do produto e o PDF vem do backend dele, com a mesma UX de
    // download dos demais — o fluxo de detalhes/PDF InsureMO não se aplica.
    const ownBackend = getOwnBackend(policy.productCode);
    if (ownBackend) {
      if (action === 'download-pdf') {
        ownBackendPolicyPdf.mutate({
          productCode: policy.productCode,
          id: policy.id,
        });
        return;
      }
      push(ownBackend.policyDetailsRoute, { proposalId: policy.id });
      return;
    }

    if (action === 'details') {
      push('/policies/$policyId', { policyId: policy.id });
      return;
    }

    if (action === 'download-pdf') {
      policyPdf.reset();
      setPendingPolicy(policy);
      setDialogOpen(true);
      return;
    }
  };

  const columns = createPoliciesColumns(handleAction, policyActionGuard);
  const policies = (data?.data ?? []) as PolicySummary[];
  const totalPages = data?.meta.totalPages ?? 0;
  const totalItems = data?.meta.total ?? 0;
  const sorting = useMemo(() => {
    const columnId = POLICY_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'desc',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <>
      <DownloadPdfDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setPendingPolicy(null);
        }}
        onConfirm={() => {
          if (pendingPolicy) {
            policyPdf.mutate({
              policyId: pendingPolicy.id,
              policyNo:
                pendingPolicy.externalPolicyNo || pendingPolicy.policyNo,
            });
          }
        }}
        isLoading={policyPdf.isPending}
        isError={policyPdf.isError}
        title='Baixar PDF'
        message='O PDF da apólice será baixado para o seu dispositivo.'
      />
      <div className='pt-4'>
        <PolicyStatusCards
          total={totalItems}
          isLoading={isLoading}
          premiumSum={data?.meta.premiumSum ?? null}
          isPremiumLoading={isLoading}
        />
      </div>

      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-5xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, status: value, pageNo: 1 })
            }
          />
          <SelectFilterField
            {...registerFilter('product')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, product: value, pageNo: 1 })
            }
          />
          <div className='flex items-center gap-3 md:contents'>
            <DateRangeFilterField
              from={filters.dateFrom}
              to={filters.dateTo}
              placeholder='Data de vigência'
              className='flex-1 md:min-w-[200px]'
              onChange={range =>
                setFilters({
                  ...filters,
                  dateFrom: range.from,
                  dateTo: range.to,
                  pageNo: 1,
                })
              }
            />
            <button
              type='button'
              onClick={() => refetch()}
              disabled={isFetching}
              className='flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 transition-colors hover:bg-alba-gray-50 hover:text-alba-gray-700 disabled:opacity-50'
              title='Atualizar'
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              />
            </button>
          </div>
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        <div className='space-y-4 md:hidden'>
          {isLoading
            ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                (_, index) => (
                  <div
                    key={index}
                    className='h-60 animate-pulse rounded-3xl bg-alba-gray-200'
                  />
                )
              )
            : policies.map(policy => {
                const statusNum = Number(policy.policyStatus);
                const config =
                  statusNum === PolicyStatus.Effective
                    ? { label: 'Emitida', colorScheme: 'success' as const }
                    : statusNum === PolicyStatus.Rejected
                      ? { label: 'Cancelada', colorScheme: 'error' as const }
                      : { label: 'Desconhecido', colorScheme: 'gray' as const };

                return (
                  <article
                    key={policy.id}
                    className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex min-w-0 items-center gap-3'>
                        <Avatar.Root
                          size='md'
                          shape='circle'
                          className='bg-alba-blue-500'
                        >
                          <User className='w-4 h-4 text-white' />
                        </Avatar.Root>
                        <div className='min-w-0'>
                          <p className='truncate font-gotham font-bold text-alba-black-500'>
                            {policy.insuredName}
                          </p>
                          {policy.insuredDocument && (
                            <p className='mt-1 text-xs text-alba-gray-600'>
                              {formatRegisterNumber(policy.insuredDocument)}
                            </p>
                          )}
                        </div>
                      </div>
                      <Menu.Root>
                        <Menu.Trigger />
                        <Menu.Content>
                          <Menu.Item
                            value='details'
                            icon={<Eye size={16} />}
                            onSelect={() => handleAction('details', policy)}
                          >
                            Ver detalhes
                          </Menu.Item>
                          <Menu.Item
                            value='download-pdf'
                            icon={<Download size={16} />}
                            disabled={policyActionGuard.blocked}
                            title={policyActionGuard.reason}
                            onSelect={() =>
                              handleAction('download-pdf', policy)
                            }
                          >
                            Baixar PDF Apólice
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Root>
                    </div>

                    <div className='mt-4 space-y-2'>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Corretor
                        </p>
                        <p className='truncate text-sm text-alba-gray-700'>
                          {(policy.brokerName || '').split('\n')[0]}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Produto
                        </p>
                        <p className='truncate text-sm text-alba-gray-700'>
                          {policy.productName}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          N° da Apólice
                        </p>
                        <p className='truncate text-sm text-alba-gray-700'>
                          {policy.externalPolicyNo || policy.policyNo}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Prêmio
                        </p>
                        <p className='truncate text-sm text-alba-gray-700 tabular-nums'>
                          {policy.duePremium
                            ? formatCurrency(policy.duePremium)
                            : '—'}
                        </p>
                      </div>
                      <div className='flex items-start justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Vigência
                        </p>
                        <div className='flex flex-col items-end gap-0.5 text-right text-sm text-alba-gray-700'>
                          <div className='flex items-center gap-1'>
                            <CalendarDays className='h-3.5 w-3.5 shrink-0 text-alba-gray-600' />
                            <span>
                              {formatDateToBrazil(policy.effectiveDate)}
                            </span>
                          </div>
                          {policy.expiryDate && (
                            <div className='flex items-center gap-1'>
                              <CalendarDays className='h-3.5 w-3.5 shrink-0 text-alba-gray-600' />
                              <span>
                                {formatDateToBrazil(policy.expiryDate)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Criado por
                        </p>
                        <p className='truncate text-sm text-alba-gray-700'>
                          {policy.inclusionUserName || '-'}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Status
                        </p>
                        <Tag.Root
                          variant='outline'
                          colorScheme={config.colorScheme}
                          size='sm'
                        >
                          <Tag.Label>{config.label}</Tag.Label>
                        </Tag.Root>
                      </div>
                    </div>
                  </article>
                );
              })}

          {!isLoading && !policies.length && (
            <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
              Nenhuma apólice encontrada.
            </div>
          )}

          {totalPages > 0 && (
            <div className='flex justify-center'>
              <Pagination
                count={totalPages * filters.pageSize}
                page={filters.pageNo}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                onPageChange={page =>
                  setFilters({
                    ...filters,
                    pageNo: page.page,
                    pageSize: page.pageSize,
                  })
                }
                onPageSizeSelect={pageSize =>
                  setFilters({ ...filters, pageNo: 1, pageSize })
                }
              />
            </div>
          )}
        </div>

        <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
          <DataTable
            ref={dataTableRef}
            className='min-w-0'
            loading={isLoading}
            columns={columns}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
            totalPages={totalPages}
            data={policies}
            pageSize={filters.pageSize}
            totalItems={totalItems}
            manualSorting
            sorting={sorting}
            onSortChange={nextSorting => {
              const nextSort = nextSorting[0];
              const sortField = nextSort
                ? POLICY_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                : DEFAULT_SORT_FIELD;

              setFilters({
                ...filters,
                pageNo: 1,
                sortField: sortField ?? DEFAULT_SORT_FIELD,
                sortType: nextSort?.desc ? 'desc' : 'asc',
              });
            }}
            onPageChange={(page, pageSize) =>
              setFilters({ ...filters, pageNo: page, pageSize })
            }
          />
        </div>
      </Page.Content>
    </>
  );
}
